import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { getFlowOptions } from "@/api";
import { EntityDetailPage } from "@/components/build/EntityDetailPage";
import { EditWithAiButton } from "@/features/build/withAi/EditWithAiButton";
import { FlowAccessTab } from "./FlowAccessTab";
import { FlowForm } from "./FlowForm";
import { FlowRunsTab } from "./FlowRunsTab";
import { FlowStepsEditor } from "./FlowStepsEditor";
import { FlowTriggersTab } from "./FlowTriggersTab";
import { RunFlowButton } from "./RunFlowButton";
import { useDeleteFlow } from "./useDeleteFlow";

/** Build > Workflows > detail: the shared EntityDetailPage scaffold; Overview (form), Steps (visual editor), Triggers, Runs, plus Run. */
export function FlowDetailPage() {
  const { t } = useTranslation(["build", "workflows"]);
  const navigate = useNavigate();
  const { uuid = "" } = useParams();
  const flow = useQuery(getFlowOptions({ path: { flow_uuid: uuid } }));
  const { onDelete, dialog } = useDeleteFlow(() => navigate("/build/workflows"));
  const data = flow.data?.data;

  return (
    <EntityDetailPage
      listPath="/build/workflows"
      rootLabel={t("workflows:title")}
      name={data?.name ?? ""}
      isPending={flow.isPending}
      isError={flow.isError}
      notFoundLabel={t("workflows:detail.notFound")}
      maxWidth="max-w-6xl"
      tabs={[
        { value: "overview", label: t("workflows:tabs.overview"), content: data && <FlowForm flow={data} /> },
        { value: "steps", label: t("workflows:tabs.steps"), content: data && <FlowStepsEditor flow={data} /> },
        { value: "triggers", label: t("workflows:tabs.triggers"), content: data && <FlowTriggersTab flow={data} /> },
        { value: "runs", label: t("workflows:tabs.runs"), content: data && <FlowRunsTab flow={data} /> },
        { value: "access", label: t("workflows:tabs.access"), content: data && <FlowAccessTab flow={data} /> },
      ]}
      canDelete={data?.canManage ?? false}
      onDelete={() => data && onDelete(data)}
      deleteDialog={dialog}
      extraActions={
        data && (
          <>
            {data.canEdit && <EditWithAiButton entityType="workflow" uuid={data.uuid} />}
            <RunFlowButton flow={data} />
          </>
        )
      }
    />
  );
}
